import React from 'react'
import { BREAKPOINTS } from '@features/General'
import ControllerFooterDesktop from './ControllerFooterDesktop'
import ControllerFooterMobile from './ControllerFooterMobile'
import { useHistory } from 'react-router'
import useWindowSize from 'hooks/processor/useWindowSize'
import findIndex from 'lodash/findIndex'
import { useSelector } from 'react-redux'
import { RootState } from '@store/store'

type InnerRouteType = {
    route: string
    skip: boolean
    back?: boolean
    onSkip?: () => void
    next?: {
        disabled?: boolean
        onClick?: () => void
        isFetching?: boolean
        text?: string
    }
    leave?: {
        text?: string
    }
    nextIsSubmit?: boolean
}

type StepType = {
    key: string
    routes: InnerRouteType[]
    stepText: string
    errorText?: string
    additionalText?: string
}

interface IControllerFooter {
    steps?: StepType[]
    keyRoute?: string
    backRoute?: string
    endAction?: () => any
    leaveAction?: () => any
    hasLeave?: boolean
}

function ControllerFooter(props: IControllerFooter) {
    const {
        steps = [],
        keyRoute = '',
        backRoute = '',
        endAction = () => undefined,
        leaveAction = () => undefined,
        hasLeave = true
    } = props
    const { width } = useWindowSize()
    const history = useHistory()
    const active: string = location.pathname.replace(`${keyRoute}/`, '')
    const activeIndex: number = steps.findIndex(item => active.includes(item?.key))
    const { data: i18n } = useSelector((state: RootState) => state.localization.locals)
    const nextStep: StepType = steps[activeIndex + 1]
    const prevStep: StepType = steps[activeIndex - 1]
    const routes: InnerRouteType[] = steps[activeIndex]?.routes
    const errorText: string = steps[activeIndex]?.errorText
    const additionalText: string = steps[activeIndex]?.additionalText
    const activeInnerIndex: number = findIndex(routes, item => {
        return location.pathname.includes(item.route)
    })
    const currentInnerRoute: InnerRouteType = routes?.[activeInnerIndex]

    const nextIsSubmit: boolean = currentInnerRoute?.nextIsSubmit || false
    const nextInnerRoute: string = routes?.[activeInnerIndex + 1]?.route
    const prevInnerRoute: string = routes?.[activeInnerIndex - 1]?.route
    const showSkip: boolean = currentInnerRoute?.skip
    const showBack: boolean = currentInnerRoute?.back && (activeIndex > 0 || activeInnerIndex > 0)
    const nextDisable: boolean = currentInnerRoute?.next?.disabled || false
    const nextIsFetching = currentInnerRoute?.next?.isFetching
    const nextText = currentInnerRoute?.next?.text
    const leaveText = currentInnerRoute?.leave?.text

    const onNextClick = () => {
        if (currentInnerRoute?.next?.onClick) {
            return currentInnerRoute.next.onClick()
        }
        if (nextStep && nextInnerRoute) {
            return history.push({
                pathname: `${keyRoute}/${nextInnerRoute}`,
                search: location.search
            })
        }

        if (nextStep && !nextInnerRoute) {
            return history.push({
                pathname: `${keyRoute}/${nextStep.routes[0].route}`,
                search: location.search
            })
        }

        history.push({
            pathname: `${keyRoute}`,
            search: location.search
        })
        return endAction()
    }

    const onBackClick = () => {
        if (backRoute) {
            return history.push({
                pathname: backRoute
            })
        }
        if (prevStep && prevInnerRoute) {
            return history.push({
                pathname: `${keyRoute}/${prevInnerRoute}`,
                search: location.search
            })
        }

        if (prevStep && !prevInnerRoute) {
            return history.push({
                pathname: `${keyRoute}/${prevStep.routes[prevStep.routes.length - 1].route}`,
                search: location.search
            })
        }

        if (!prevStep && prevInnerRoute) {
            return history.push({
                pathname: `${keyRoute}/${prevInnerRoute}`,
                search: location.search
            })
        }
    }

    const onLeaveClick = () => {
        leaveAction()
    }

    const onSkipClick = () => {
        if (typeof currentInnerRoute.onSkip === 'undefined') {
            return onNextClick()
        }

        currentInnerRoute.onSkip()
    }

    const stepArray = steps.map(item => ({ title: item.stepText, route: `${keyRoute}/${item.routes[0].route}` }))
    if (width < BREAKPOINTS.LG) {
        return (
            <ControllerFooterMobile
                nextDisable={nextDisable}
                nextIsFetching={nextIsFetching}
                hasSkip={showSkip}
                hasBack={showBack}
                nextText={nextText}
                onBack={onBackClick}
                onLeave={onLeaveClick}
                onNext={onNextClick}
                onSkip={onSkipClick}
                hasLeave={hasLeave}
                i18n={i18n}
                errorText={errorText}
                additionalText={additionalText}
                nextIsSubmit={nextIsSubmit}
                stepArray={stepArray}
                activeStep={activeIndex}
            />
        )
    }

    return (
        <ControllerFooterDesktop
            stepArray={stepArray}
            nextIsFetching={nextIsFetching}
            nextDisable={nextDisable}
            hasSkip={showSkip}
            hasBack={showBack}
            activeStep={activeIndex}
            leaveText={leaveText}
            nextText={nextText}
            onBack={onBackClick}
            onLeave={onLeaveClick}
            onNext={onNextClick}
            onSkip={onSkipClick}
            hasLeave={hasLeave}
            i18n={i18n}
            errorText={errorText}
            additionalText={additionalText}
            nextIsSubmit={nextIsSubmit}
        />
    )
}

export default ControllerFooter
